// Themed Alert Component - replaces JavaScript alerts
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Alert.css';

type AlertType = 'info' | 'success' | 'warning' | 'error';

export interface AlertAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
}

interface AlertProps {
  message: string;
  type?: AlertType;
  onClose: () => void;
  autoClose?: boolean;
  duration?: number;
  suggestions?: string[];
  actions?: AlertAction[];
}

const Alert: React.FC<AlertProps> = ({ message, type = 'info', onClose, autoClose = false, duration = 5000, suggestions, actions }) => {
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoClose, duration, onClose]);

  const getIcon = (): string => {
    switch (type) {
      case 'error':
        return '!';
      case 'warning':
        return '!';
      case 'success':
        return '✓';
      case 'info':
      default:
        return 'i';
    }
  };

  const hasExtras = (suggestions && suggestions.length > 0) || (actions && actions.length > 0);

  return ReactDOM.createPortal(
    <div className="alert-overlay" onClick={onClose}>
      <div className={`alert-dialog alert-${type}${hasExtras ? ' alert-with-suggestions' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="alert-icon">{getIcon()}</div>
        <div className="alert-content">
          <p className="alert-message">{message}</p>
          {suggestions && suggestions.length > 0 && (
            <div className="alert-suggestions">
              <p className="alert-suggestions-title">💡 Suggestions:</p>
              <ul className="alert-suggestions-list">
                {suggestions.map((suggestion, index) => (
                  <li key={index}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}
          {actions && actions.length > 0 && (
            <div className="alert-actions">
              {actions.map((action, index) => (
                <button
                  key={index}
                  className={`alert-action-btn alert-action-${action.variant ?? 'primary'}`}
                  onClick={() => { action.onClick(); onClose(); }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button className="alert-close" onClick={onClose}>
          ×
        </button>
      </div>
    </div>,
    document.body
  );
};

interface AlertData {
  message: string;
  type: AlertType;
  autoClose: boolean;
  duration?: number;
  suggestions?: string[];
  actions?: AlertAction[];
}

type AlertListener = (alertData: AlertData) => void;

// Alert service for imperative usage
class AlertService {
  private listeners: AlertListener[] = [];

  show(message: string, type: AlertType = 'info', autoClose: boolean = true, suggestions?: string[], duration?: number, actions?: AlertAction[]): void {
    this.listeners.forEach(listener => listener({ message, type, autoClose, duration, suggestions, actions }));
  }

  info(message: string, autoClose: boolean = true, duration?: number): void {
    this.show(message, 'info', autoClose, undefined, duration);
  }

  success(message: string, autoClose: boolean = true, duration?: number): void {
    this.show(message, 'success', autoClose, undefined, duration);
  }

  warning(message: string, autoClose: boolean = true, duration?: number): void {
    this.show(message, 'warning', autoClose, undefined, duration);
  }

  error(message: string, autoClose: boolean = false, suggestions?: string[], duration?: number, actions?: AlertAction[]): void {
    this.show(message, 'error', autoClose, suggestions, duration, actions);
  }

  subscribe(listener: AlertListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }
}

export const alertService = new AlertService();
export default Alert;
